import React, { useState } from 'react';
import { Settings, X } from 'lucide-react';
import Button from './Button';

export default function FlowchartSettings({ settings, onSettingsChange, onClose }) {
  const [localSettings, setLocalSettings] = useState(settings);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onSettingsChange(localSettings);
    onClose();
  };

  const handleReset = () => {
    const defaultSettings = {
      algorithm: 'dagre',
      direction: 'LR',
      density: 'standard',
      edgeType: 'default'
    };
    setLocalSettings(defaultSettings);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Настройки отображения</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Алгоритм раскладки */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Алгоритм раскладки
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleChange('algorithm', 'dagre')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.algorithm === 'dagre'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Dagre
              </button>
              <button
                onClick={() => handleChange('algorithm', 'elk')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.algorithm === 'elk'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ELK
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {localSettings.algorithm === 'dagre' 
                ? 'Быстрый и эффективный алгоритм'
                : 'Улучшенные алгоритмы для сложных графов'}
            </p>
          </div>

          {/* Направление раскладки */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Направление
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleChange('direction', 'LR')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.direction === 'LR'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Слева направо →
              </button>
              <button
                onClick={() => handleChange('direction', 'RL')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.direction === 'RL'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Справа налево ←
              </button>
              <button
                onClick={() => handleChange('direction', 'TB')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.direction === 'TB'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Сверху вниз ↓
              </button>
              <button
                onClick={() => handleChange('direction', 'BT')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.direction === 'BT'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Снизу вверх ↑
              </button>
            </div>
          </div>

          {/* Плотность */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Плотность раскладки
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleChange('density', 'compact')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.density === 'compact'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Компакт
              </button>
              <button
                onClick={() => handleChange('density', 'standard')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.density === 'standard'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Стандарт
              </button>
              <button
                onClick={() => handleChange('density', 'spacious')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.density === 'spacious'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Просторно
              </button>
            </div>
          </div>

          {/* Тип связей */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Тип связей
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleChange('edgeType', 'default')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.edgeType === 'default'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Прямые
              </button>
              <button
                onClick={() => handleChange('edgeType', 'smoothstep')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.edgeType === 'smoothstep'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Сглаженные
              </button>
              <button
                onClick={() => handleChange('edgeType', 'step')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.edgeType === 'step'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ступенчатые
              </button>
              <button
                onClick={() => handleChange('edgeType', 'simplebezier')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  localSettings.edgeType === 'simplebezier'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Кривые
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <Button
            onClick={handleReset}
            variant="outline"
            className="flex-1"
          >
            Сбросить
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1"
          >
            Применить
          </Button>
        </div>
      </div>
    </div>
  );
}
